const BattleCalc = require('../BattleCalc');
const Balance = require('../../constants/Balance');
const DamageEvent = require('../events/DamageEvent');
const StandardStrike = require('../abilities/StandardStrike');

const AICalc = {
  SUPPORT_DECISION_LOW_HP_THRESHOLD: 80, // percent

  getBestMeleeTarget: function (actor, battleState) {
    let candidates = battleState.getEnemiesOfUnit(actor);
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }

    // heroes only target heroes in PvP
    if (!battleState.isCaverns && actor.hero && !actor.poppet) {
      candidates = candidates.filter(({ hero, poppet }) => hero && !poppet);
    }
    if (!candidates?.length) return null;

    let bestTarget = candidates[0];
    let bestDist = BattleCalc.boardDistance(actor, bestTarget);

    for (const enemy of candidates) {
      if (!enemy.inPlay) {
        continue;
      }

      const dist = BattleCalc.boardDistance(actor, enemy);

      if (
        dist < bestDist ||
        (dist === bestDist &&
          AICalc.getEffectiveHealthRemaining({
            attacker: actor,
            defender: enemy,
          }) <
            AICalc.getEffectiveHealthRemaining({
              attacker: actor,
              defender: bestTarget,
            }))
      ) {
        bestTarget = enemy;
        bestDist = dist;
      }
    }

    return bestTarget;
  },

  getBestMissileTarget: function (actor, battleState) {
    let candidates = battleState.getEnemiesOfUnit(actor);
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }

    // heroes only target heroes in PvP
    if (!battleState.isCaverns && actor.hero && !actor.poppet) {
      candidates = candidates.filter(({ hero, poppet }) => hero && !poppet);
    }

    if (!candidates?.length) {
      return null;
    }

    // figure out what my lowest range ability is
    let least_range = 50;
    for (const ability_handle of [
      'arrow_shot',
      'razor_shot',
      'void_spike',
      'infect_mind',
      'molotov_cocktail',
      'multi_shot',
      'fumigate',
      'lightning',
      'chain_pull',
    ]) {
      const ability = BattleCalc.getEquippedAbility(actor, ability_handle);
      if (ability && ability.range.max < least_range) {
        least_range = ability.range.max;
      }
    }

    return getLowestHealthCandidateInRange({ range: least_range, candidates });

    function getLowestHealthCandidateInRange({ range, candidates }) {
      let lowest_health = Infinity;
      let result;
      // find candidate lowest health within range (if nx, increment range)
      for (let r = 1; r <= range; r++) {
        for (const candidate of candidates) {
          if (BattleCalc.boardDistance(actor, candidate) <= r) {
            const health = AICalc.getEffectiveHealthRemaining({
              attacker: actor,
              defender: candidate,
            });
            if (health < lowest_health) {
              lowest_health = health;
              result = candidate;
            }
          }
        }
      }

      return result
        ? result
        : range < 25
          ? getLowestHealthCandidateInRange({ range: range + 1, candidates })
          : AICalc.getBestMeleeTarget(actor, battleState);
    }
  },

  getBestDoTTarget(actor, initial_target, battleState) {
    let dot_ability_dist = 0;
    for (const ability_handle of ['razor_shot', 'infect_mind', 'fumigate']) {
      const ability = BattleCalc.getEquippedAbility(actor, ability_handle);
      if (ability && ability.range.max > dot_ability_dist) {
        dot_ability_dist = ability.range.max;
        break;
      }
    }

    // in Caverns, spread DoTs around to maximize AoE damage
    if (battleState.isCaverns) {
      let least_potency = BattleCalc.getCombinedDoTPotency(initial_target);
      let least_dotted_unit = initial_target;

      const allies_of_target = BattleCalc.getAlliesOfUnit(
        initial_target,
        battleState.allUnits,
        true
      );

      for (const ally of allies_of_target) {
        const combined_potency = BattleCalc.getCombinedDoTPotency(ally);
        if (
          combined_potency < least_potency &&
          BattleCalc.boardDistance(ally, actor) <= dot_ability_dist
        ) {
          least_potency = combined_potency;
          least_dotted_unit = ally;
        }
      }

      return least_dotted_unit;
    } else {
      // in PvP, focus one target with DoTs
      return AICalc.getBestMissileTarget(actor, battleState);
    }
  },

  defineEnemyCluster: function (target, candidates) {
    if (!Array.isArray(candidates) && typeof candidates === 'object') {
      candidates = Object.values(candidates);
    }
    return candidates.filter((enemy) => BattleCalc.boardDistance(enemy, target) < 3);
  },

  getNearbyUnits: function (subject, candidates, defineNearby) {
    var nearbyUnits = [];
    for (var i = 0; i < candidates.length; ++i) {
      var unit = candidates[i];
      if (BattleCalc.boardDistance(subject, unit) <= defineNearby) {
        nearbyUnits.push(unit);
      }
    }

    return nearbyUnits;
  },

  getBestSummonTile(actor, battleState) {
    var tile = null;
    var candidateTiles = [];
    var attempts = 0;

    var distFromActor = 1;
    do {
      for (var tid in battleState.allTiles) {
        var tile = battleState.allTiles[tid];
        if (BattleCalc.boardDistance(tile, actor) <= distFromActor && !tile.occupied) {
          candidateTiles.push(tile);
        }
      }
      ++distFromActor;
    } while (
      distFromActor <= Balance.CONJURATION_RANGE &&
      candidateTiles.length === 0 &&
      ++attempts < 50
    );

    if (candidateTiles.length === 0) {
      return null;
    } else {
      return candidateTiles[Math.floor(Math.random() * candidateTiles.length)];
    }
  },

  getEffectiveHealthRemaining({ attacker, defender }) {
    // (defender_hp / ( stance_mod * armor_mod * affinity_mod )
    return (
      defender.hp /
      (BattleCalc.getDamageModFromStances({
        instigator: attacker,
        victim: defender,
      }) *
        (1 - Balance.getDmgReduxFromArmorClass(defender.attributes.armorClass)) *
        BattleCalc.getDamageModFromAffinities(attacker, defender))
    );
  },

  /**
   * Predicts total damage an AoE ability would deal to enemies vs allies
   * Uses the actual ability damage formulas with DamageEvent simulation (is_dummy=true)
   *
   * @param {Object} actor - The unit casting the ability
   * @param {string} abilityHandle - The ability handle (e.g., 'roundhouse', 'molotov_cocktail')
   * @param {Object} target - The target location/unit (used for targeted AoE like molotov)
   * @param {Object} battleState - The current battle state
   * @returns {Object} { totalEnemyDamage, totalAllyDamage, damageByUnit: Map<uid, damage> }
   */
  predictAbilityDamage(actor, abilityHandle, target, battleState) {
    const ability = BattleCalc.getEquippedAbility(actor, abilityHandle);
    if (!ability) {
      return { totalEnemyDamage: 0, totalAllyDamage: 0, damageByUnit: new Map() };
    }

    const damageByUnit = new Map();
    let totalEnemyDamage = 0;
    let totalAllyDamage = 0;

    // Create a mock ability object with the necessary properties
    const mockAbility = {
      handle: abilityHandle,
      physical: true,
      weapon: abilityHandle === 'roundhouse',
      melee: abilityHandle === 'roundhouse',
      ranged: ['molotov_cocktail', 'multi_shot'].includes(abilityHandle),
      magic: abilityHandle === 'lightning',
      harm: true,
      bow: abilityHandle === 'multi_shot',
    };

    // Get units to check
    let enemies = battleState.getEnemiesOfUnit(actor);
    let allies = BattleCalc.getAlliesOfUnit(actor, battleState.allUnits, false);

    // for PvP only consider heroes
    if (actor.hero && !battleState.isCaverns) {
      enemies = enemies.filter(({ hero, poppet }) => hero && !poppet);
      allies = allies.filter(({ hero, poppet }) => hero && !poppet);
    }

    // Helper to simulate damage for a unit
    const simulateDamageToUnit = (targetUnit, baseDamage) => {
      if (!targetUnit || targetUnit.dead || !targetUnit.inPlay) {
        return 0;
      }

      // Create a dummy StandardStrike to simulate the damage
      const standardStrike = new StandardStrike(mockAbility, battleState);
      const originalHp = targetUnit.hp;

      // Execute with is_dummy=true to avoid side effects
      standardStrike.execute(actor, targetUnit, baseDamage, false, true);

      // Calculate damage dealt
      const damageDealt = originalHp - targetUnit.hp;

      // Restore original HP (since is_dummy still modifies hp)
      targetUnit.hp = originalHp;

      return Math.max(0, damageDealt);
    };

    // Ability-specific damage calculations
    switch (abilityHandle) {
      case 'roundhouse':
      case 'lightning': {
        const RANGE = 2;
        const baseDamage = ability.damage;

        for (const enemy of enemies) {
          const dist = BattleCalc.boardDistance(actor, enemy);
          if (dist > 0 && dist <= RANGE) {
            const damage = simulateDamageToUnit(enemy, baseDamage / dist);
            damageByUnit.set(enemy.uid, damage);
            totalEnemyDamage += damage;
          }
        }

        for (const ally of allies) {
          const dist = BattleCalc.boardDistance(actor, ally);
          if (dist > 0 && dist <= RANGE) {
            const damage = simulateDamageToUnit(ally, baseDamage / dist);
            damageByUnit.set(ally.uid, damage);
            totalAllyDamage += damage;
          }
        }
        break;
      }

      case 'molotov_cocktail': {
        const baseDamage = ability.damage;
        const targetLocation = target || enemies[0];

        if (!targetLocation) {
          break;
        }

        for (const enemy of enemies) {
          const dist = BattleCalc.boardDistance(targetLocation, enemy);
          const adjustedDamage = baseDamage - (baseDamage * dist) / 4;
          if (adjustedDamage > 5) {
            const damage = simulateDamageToUnit(enemy, adjustedDamage);
            damageByUnit.set(enemy.uid, damage);
            totalEnemyDamage += damage;
          }
        }

        for (const ally of allies) {
          const dist = BattleCalc.boardDistance(targetLocation, ally);
          const adjustedDamage = baseDamage - (baseDamage * dist) / 4;
          if (adjustedDamage > 5) {
            const damage = simulateDamageToUnit(ally, adjustedDamage);
            damageByUnit.set(ally.uid, damage);
            totalAllyDamage += damage;
          }
        }
        break;
      }

      case 'fumigate': {
        const range = ability.effectRange;
        const baseDamage = ability.potency;

        for (const enemy of enemies) {
          const dist = BattleCalc.boardDistance(actor, enemy);
          if (dist <= range) {
            // Fumigate is a DoT, so estimate total damage
            const damage = baseDamage * 3; // rough estimate of total DoT damage
            damageByUnit.set(enemy.uid, damage);
            totalEnemyDamage += damage;
          }
        }

        for (const ally of allies) {
          const dist = BattleCalc.boardDistance(actor, ally);
          if (dist <= range) {
            const damage = baseDamage * 3;
            damageByUnit.set(ally.uid, damage);
            totalAllyDamage += damage;
          }
        }
        break;
      }

      default:
        // Unknown ability, return zeros
        break;
    }

    return { totalEnemyDamage, totalAllyDamage, damageByUnit };
  },

  /**
   * Determines if an ability should be used based on predicted damage
   *
   * @param {Object} actor - The unit casting the ability
   * @param {string} abilityHandle - The ability handle
   * @param {Object} target - The target location/unit
   * @param {Object} battleState - The current battle state
   * @param {number} requiredRatio - Minimum ratio of enemy:ally damage (default 1.5)
   * @returns {boolean} True if the ability should be used
   */
  isAoESafe(actor, abilityHandle, target, battleState, requiredRatio = 1.5) {
    const { totalEnemyDamage, totalAllyDamage } = AICalc.predictAbilityDamage(
      actor,
      abilityHandle,
      target,
      battleState
    );

    // If there are no allies in range, use the ability if it hits enemies
    if (totalAllyDamage === 0) {
      return totalEnemyDamage > 0;
    }

    // Only use if enemy damage is at least requiredRatio times ally damage
    return totalEnemyDamage >= totalAllyDamage * requiredRatio;
  },
};
module.exports = AICalc;
